// Projection de trésorerie (Pilotage) — réglages et corrections manuelles.
//
// Le raisonnement vit dans @imp/shared/projection-tresorerie, pur et testé.
// Ce module ne fait que LIRE et ÉCRIRE les deux choses qui doivent survivre au
// rafraîchissement de la page : les réglages du calcul, et les corrections que
// l'utilisateur apporte ligne à ligne (« celui-là est déjà payé », « celui-là
// tombera le 15 novembre »). Sans elles, la projection serait à refaire à
// chaque visite.

import {
  type AjustementEncaissement,
  type EtatEncaissement,
  REGLES_PAR_DEFAUT,
  type ReglesProjection,
} from '@imp/shared/projection-tresorerie';

import { lireTout } from '@/lib/pagination';
import { supabase } from '@/lib/supabase';
import { ORG_ID } from '@/modules/clients/api';

export type { AjustementEncaissement, EtatEncaissement, ReglesProjection };

/** Réglages du calcul pour une année ; ceux par défaut si rien n'a été réglé. */
export async function getReglesProjection(annee: number): Promise<ReglesProjection> {
  const { data, error } = await supabase
    .from('treso_soldes')
    .select('projection_delai_jours,projection_taux_tva,projection_part_acompte')
    .eq('annee', annee);
  if (error) throw new Error(error.message);
  const l = (data as Record<string, unknown>[] | null)?.[0];
  if (!l) return REGLES_PAR_DEFAUT;
  return {
    delaiJours: Number(l.projection_delai_jours ?? REGLES_PAR_DEFAUT.delaiJours),
    tauxTva: Number(l.projection_taux_tva ?? REGLES_PAR_DEFAUT.tauxTva),
    partAcompte: Number(l.projection_part_acompte ?? REGLES_PAR_DEFAUT.partAcompte),
  };
}

export async function setReglesProjection(
  annee: number,
  regles: ReglesProjection,
): Promise<void> {
  // Upsert sur (organization_id, annee) : la ligne porte aussi le solde de
  // départ, qu'il ne faut surtout pas écraser — il n'est donc pas dans la
  // charge utile, et une mise à jour ne touche que les colonnes citées.
  const { error } = await supabase.from('treso_soldes').upsert(
    {
      organization_id: ORG_ID,
      annee,
      projection_delai_jours: regles.delaiJours,
      projection_taux_tva: regles.tauxTva,
      projection_part_acompte: regles.partAcompte,
    },
    { onConflict: 'organization_id,annee' },
  );
  if (error) throw new Error(error.message);
}

export interface AjustementLigne extends AjustementEncaissement {
  chantierId: string;
}

export async function listAjustements(): Promise<AjustementLigne[]> {
  // Lu par tranches : une lecture tronquée ferait réapparaître dans la
  // projection un encaissement marqué « déjà reçu », et gonflerait le solde.
  const lignes = await lireTout<{
    chantier_id: string;
    etat: string;
    date_prevue: string | null;
    montant_prevu: string | number | null;
    note: string | null;
  }>((de, a) =>
    supabase
      .from('treso_encaissements_prevus')
      .select('chantier_id,etat,date_prevue,montant_prevu,note')
      .eq('organization_id', ORG_ID)
      .is('deleted_at', null)
      .order('chantier_id')
      .range(de, a),
  );
  return lignes.map((l) => ({
    chantierId: l.chantier_id,
    etat: estEtat(l.etat) ? l.etat : 'attendu',
    datePrevue: l.date_prevue,
    montantPrevu: l.montant_prevu === null ? null : Number(l.montant_prevu),
    note: l.note,
  }));
}

function estEtat(v: string): v is EtatEncaissement {
  return v === 'attendu' || v === 'encaisse' || v === 'exclu';
}

/**
 * Enregistre la correction d'une ligne.
 *
 * Le contenu complet est réécrit à chaque fois plutôt que fusionné : l'appelant
 * connaît l'état de la ligne qu'il affiche, et un `merge` partiel laisserait
 * traîner une date forcée qu'on croyait avoir effacée.
 */
export async function enregistrerAjustement(
  chantierId: string,
  ajustement: AjustementEncaissement,
): Promise<void> {
  const { error } = await supabase.from('treso_encaissements_prevus').upsert(
    {
      organization_id: ORG_ID,
      chantier_id: chantierId,
      etat: ajustement.etat,
      date_prevue: ajustement.datePrevue,
      montant_prevu: ajustement.montantPrevu,
      note: ajustement.note,
      deleted_at: null,
    },
    { onConflict: 'organization_id,chantier_id' },
  );
  if (error) throw new Error(error.message);
}

/** Efface la correction : la ligne repasse au calcul automatique. */
export async function effacerAjustement(chantierId: string): Promise<void> {
  const { error } = await supabase
    .from('treso_encaissements_prevus')
    .delete()
    .eq('organization_id', ORG_ID)
    .eq('chantier_id', chantierId);
  if (error) throw new Error(error.message);
}
